import { useLayoutEffect, useState, type RefObject } from 'react';

/** The phone storefront switches to the desktop grid here (PackLayout). */
const DESKTOP_MIN_WIDTH_PX = 800;

/** Screen height the page gets — the viewport minus the host insets `#root`
 *  pads with (status bar, home indicator). */
function screenHeight(): number {
  const root = document.getElementById('root');
  const style = root ? getComputedStyle(root) : null;
  const insets = style ? (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0) : 0;
  return document.documentElement.clientHeight - insets;
}

function headerHeight(): number {
  return document.querySelector<HTMLElement>('header[data-co-component="Header"]')?.offsetHeight ?? 0;
}

/**
 * Height (px) the phone's slider may take so the whole storefront fits one
 * screen (repackz-handoff §8.2): the screen minus the header and everything
 * in the layout but the slider. That remainder doesn't depend on the
 * slider's own height, so growing the slider never feeds back into it.
 * `null` on the desktop grid.
 */
export function usePhoneSliderRoom(
  layoutRef: RefObject<HTMLElement | null>,
  sliderRef: RefObject<HTMLElement | null>,
): number | null {
  const [room, setRoom] = useState<number | null>(null);

  useLayoutEffect(() => {
    const layout = layoutRef.current;
    const slider = sliderRef.current;
    if (!layout || !slider) return;

    const measure = () => {
      if (document.documentElement.clientWidth >= DESKTOP_MIN_WIDTH_PX) {
        setRoom(null);
        return;
      }
      const chrome = headerHeight() + layout.offsetHeight - slider.offsetHeight;
      setRoom(Math.max(0, screenHeight() - chrome));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(layout);
    observer.observe(document.documentElement);
    return () => observer.disconnect();
  }, [layoutRef, sliderRef]);

  return room;
}
